// src/components/Navbar.js

import React, { useState } from 'react'
import {
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button as MuiButton,
  Typography
} from '@mui/material'

import HelpIcon from '@mui/icons-material/Help';

import { PlayArrow, ArrowBack, Close } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

import {
  Menu as MenuIcon,
  AdminPanelSettings as AdminIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Person as ProfileIcon,
  RateReview as RateReviewIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

// Çoklu dil desteği
import { useTranslation } from 'react-i18next'

export default function Navbar() {
  const navigate = useNavigate()
  const { currentUser, userRole, logout } = useAuth()
  const { t, i18n } = useTranslation()
  const theme = useTheme();

  // Drawer (hamburger) menü kontrolü
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Add state for help modal and selected video
  const [helpModalOpen, setHelpModalOpen] = useState(false)
  const [selectedVideo, setSelectedVideo] = useState(null)

  // Video URLs (replace with actual URLs)
  const videoUrls = [
    'https://codini.evoosurvey.com/videos/0.mp4',
    'https://codini.evoosurvey.com/videos/1.mp4',
    'https://codini.evoosurvey.com/videos/2.mp4',
    'https://codini.evoosurvey.com/videos/3.mp4',
    'https://codini.evoosurvey.com/videos/4.mp4',
    'https://codini.evoosurvey.com/videos/5.mp4',
    'https://codini.evoosurvey.com/videos/6.mp4',
    'https://codini.evoosurvey.com/videos/7.mp4',
  ]

  // Profil menüsü için anchor
  const [anchorEl, setAnchorEl] = useState(null)
  const openProfile = Boolean(anchorEl)

  // Raporlar menüsü için anchor
  const [raporlarAnchorEl, setRaporlarAnchorEl] = useState(null)
  const openRaporlar = Boolean(raporlarAnchorEl)

  // Ayarlar menüsü için anchor
  const [ayarlarAnchorEl, setAyarlarAnchorEl] = useState(null)
  const openAyarlar = Boolean(ayarlarAnchorEl)

  const navPalette = {
    background: 'linear-gradient(90deg, #0f172a 0%, #1e3a8a 100%)',
    text: '#e2e8f0',
    subtle: 'rgba(148, 163, 184, 0.18)',
    hover: 'rgba(96, 165, 250, 0.16)',
    accent: '#60a5fa',
    border: 'rgba(148, 163, 184, 0.24)',
  }

  const desktopButtonSx = {
    fontSize: '0.72rem',
    minWidth: 'auto',
    px: 1.25,
    py: 0.75,
    borderRadius: 2,
    color: navPalette.text,
    letterSpacing: 0.3,
    transition: 'all 0.2s ease',
    '& .MuiButton-startIcon': {
      mr: 0.75,
    },
    '&:hover': {
      backgroundColor: navPalette.hover,
      color: '#f8fafc',
    },
  }

  const languageButtonSx = {
    fontSize: '0.7rem',
    minWidth: 'auto',
    borderRadius: 2,
    px: 0.9,
    py: 0.35,
    borderColor: navPalette.border,
    color: navPalette.text,
    letterSpacing: 0.2,
    '&:hover': {
      borderColor: navPalette.accent,
      backgroundColor: 'rgba(96, 165, 250, 0.12)',
    },
  }

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen)
  }

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = async () => {
    try {
      navigate('/login')
      await logout()
    } catch (error) {
      console.error('Çıkış yapılırken hata:', error)
    }
    handleProfileMenuClose()
  }

  // Fare ile "Raporlar" menüsünün üzerine gelince aç
  const handleRaporlarMouseEnter = (event) => {
    setRaporlarAnchorEl(event.currentTarget)
  }
  // Fare "Raporlar"dan ayrılınca kapat
  const handleRaporlarMouseLeave = () => {
    setRaporlarAnchorEl(null)
  }

  // Fare ile "Ayarlar" menüsünün üzerine gelince aç
  const handleAyarlarMouseEnter = (event) => {
    setAyarlarAnchorEl(event.currentTarget)
  }
  // Fare "Ayarlar"dan ayrılınca kapat
  const handleAyarlarMouseLeave = () => {
    setAyarlarAnchorEl(null)
  }

  // Dil değiştirme fonksiyonu
  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng)
  }

  // Ana menüdeki öğeler
  const menuItems = [
  
    // Ayarlar (alt menü)
    {
      text: t('navbar.home'),
      icon: <RateReviewIcon fontSize="small" />,
      onClick: () => navigate('/ProjectsPage'),
      visible: userRole === 'user' || userRole === 'admin',
    },
    {
      text: t('navbar.userManagement'),
      icon: <VisibilityIcon fontSize="small" />,
      onClick: () => navigate('/admin-users'),
      visible: userRole === 'admin',
    }

  ]

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundImage: navPalette.background,
          color: navPalette.text,
          borderBottom: `1px solid ${navPalette.border}`,
          boxShadow: '0 6px 16px rgba(15, 23, 42, 0.18)',
        }}
      >
        <Toolbar
          variant="dense"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: { xs: 1.25, md: 2 },
            py: { xs: 0.75, md: 0.9 },
            minHeight: 'auto',
            gap: { xs: 1, md: 1.5 },
          }}
        >
          {/* Mobil Hamburger butonu */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                mr: 1,
                borderRadius: 2,
                border: `1px solid ${navPalette.border}`,
                backgroundColor: 'rgba(15, 23, 42, 0.35)',
                color: navPalette.text,
                '&:hover': {
                  backgroundColor: navPalette.hover,
                },
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Orta kısım: Logo */}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box
              component="img"
              alt="Sia-Insight"
              src="logo.png"
              sx={{
                width: 110,
                height: 'auto',
                maxWidth: '46%',
                cursor: 'pointer',
                filter: 'drop-shadow(0 3px 10px rgba(15, 23, 42, 0.4))',
              }}
              onClick={() => navigate('/ProjectsPage')}
            />
          </Box>

          {/* Sağda: Büyük ekranda menü */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            {menuItems.map((item, index) =>
              item.visible ? (
                item.subMenu ? (
                  // Eğer subMenu varsa (Raporlar veya Ayarlar), hover ile açılacak
                  <Box
                    key={index}
                    onMouseEnter={
                      // Buradaki kontrol, `item.text`in çeviri olduğu için,
                      // metni değil orijinalde hangi menü olduğuna bakabiliriz.
                      item.text === t('navbar.reports')
                        ? handleRaporlarMouseEnter
                        : handleAyarlarMouseEnter
                    }
                    onMouseLeave={
                      item.text === t('navbar.reports')
                        ? handleRaporlarMouseLeave
                        : handleAyarlarMouseLeave
                    }
                  >
                    <Button
                      color="inherit"
                      startIcon={item.icon}
                      sx={{
                        ...desktopButtonSx,
                        ml: index === 0 ? 0 : 0.5,
                      }}
                    >
                      {item.text}
                    </Button>

                    {/* Alt menü (Raporlar veya Ayarlar) */}
                    <Menu
                      anchorEl={
                        item.text === t('navbar.reports')
                          ? raporlarAnchorEl
                          : ayarlarAnchorEl
                      }
                      open={
                        item.text === t('navbar.reports')
                          ? openRaporlar
                          : openAyarlar
                      }
                      onClose={
                        item.text === t('navbar.reports')
                          ? handleRaporlarMouseLeave
                          : handleAyarlarMouseLeave
                      }
                      MenuListProps={{
                        onMouseEnter: () =>
                          item.text === t('navbar.reports')
                            ? setRaporlarAnchorEl(raporlarAnchorEl)
                            : setAyarlarAnchorEl(ayarlarAnchorEl),
                        onMouseLeave:
                          item.text === t('navbar.reports')
                            ? handleRaporlarMouseLeave
                            : handleAyarlarMouseLeave,
                        style: { pointerEvents: 'auto' }, // Hover çalışması için
                      }}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                      PaperProps={{
                        sx: {
                          mt: 0.5,
                          borderRadius: 2,
                          border: `1px solid ${navPalette.border}`,
                          backgroundColor: 'rgba(15, 23, 42, 0.97)',
                          color: navPalette.text,
                          boxShadow: '0 12px 24px rgba(15, 23, 42, 0.28)',
                          '& .MuiMenuItem-root': {
                            fontSize: '0.72rem',
                            gap: 0.75,
                            borderRadius: 1.5,
                            '&:hover': {
                              backgroundColor: 'rgba(96, 165, 250, 0.18)',
                            },
                          },
                        },
                      }}
                    >
                      {item.subMenu.map(
                        (sub, subIndex) =>
                          sub.visible && (
                            <MenuItem
                              key={subIndex}
                              onClick={() => {
                                sub.onClick()
                                // Alt menü kapat
                                item.text === t('navbar.reports')
                                  ? handleRaporlarMouseLeave()
                                  : handleAyarlarMouseLeave()
                              }}
                              sx={{ fontSize: '0.72rem' }}
                            >
                              <ListItemIcon sx={{ minWidth: 28, color: navPalette.accent }}>
                                {sub.icon}
                              </ListItemIcon>
                              {sub.text}
                            </MenuItem>
                          )
                      )}
                    </Menu>
                  </Box>
                ) : (
                  // Normal menü öğesi (subMenu yoksa)
                  <Button
                    key={index}
                    color="inherit"
                    sx={{
                      ...desktopButtonSx,
                      ml: index === 0 ? 0 : 0.5,
                    }}
                    startIcon={item.icon}
                    onClick={item.onClick}
                  >
                    {item.text}
                  </Button>
                )
              ) : null
            )}
          </Box>

          {/* Dil Değiştirme Butonları (Büyük Ekranda) */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: 0.5,
              ml: 2,
            }}
          >
            <Button variant="outlined" onClick={() => changeLanguage('en')} sx={languageButtonSx}>
              EN
            </Button>
            <Button variant="outlined" onClick={() => changeLanguage('tr')} sx={languageButtonSx}>
              TR
            </Button>
            <Button variant="outlined" onClick={() => changeLanguage('de')} sx={languageButtonSx}>
              DE
            </Button>
            <Button variant="outlined" onClick={() => changeLanguage('fr')} sx={languageButtonSx}>
              FR
            </Button>
            <Button variant="outlined" onClick={() => changeLanguage('es')} sx={languageButtonSx}>
              ES
            </Button>
            <Button variant="outlined" onClick={() => changeLanguage('it')} sx={languageButtonSx}>
              IT
            </Button>
          </Box>

          {/* Sağda: Profil Avatar veya Giriş butonu */}
          <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
            {currentUser && (
              <Tooltip title={t('navbar.help')}>
                <Button
                  color="inherit"
                  startIcon={<HelpIcon />}
                  onClick={() => setHelpModalOpen(true)}
                  sx={{
                    ...desktopButtonSx,
                    px: 1,
                    py: 0.6,
                    border: `1px solid ${navPalette.border}`,
                    backgroundColor: 'rgba(15, 23, 42, 0.28)',
                    '&:hover': {
                      backgroundColor: navPalette.hover,
                    },
                    [theme.breakpoints.down('sm')]: {
                      padding: '6px',
                      minWidth: 'auto',
                      borderRadius: 1.5,
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                    {t('navbar.help')}
                  </Box>
                </Button>
              </Tooltip>
            )}
            {currentUser ? (
              <>
                <Tooltip title={t('navbar.profile')}>
                  <IconButton
                    onClick={handleProfileMenuOpen}
                    sx={{
                      ml: 1,
                      borderRadius: 2.5,
                      border: `1px solid ${navPalette.border}`,
                      backgroundColor: 'rgba(15, 23, 42, 0.28)',
                      p: 0.3,
                      '&:hover': {
                        backgroundColor: navPalette.hover,
                      },
                    }}
                  >
                    <Avatar
                      alt={currentUser.nickname || currentUser.email}
                      src={currentUser.avatarUrl || ''}
                      sx={{
                        bgcolor: 'rgba(96, 165, 250, 0.3)',
                        color: navPalette.text,
                        width: 34,
                        height: 34,
                        border: `1px solid ${navPalette.border}`,
                        fontSize: '0.85rem',
                      }}
                    >
                      {currentUser.nickname
                        ? currentUser.nickname.charAt(0)
                        : currentUser.email.charAt(0)}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={anchorEl}
                  open={openProfile}
                  onClose={handleProfileMenuClose}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      borderRadius: 2,
                      border: `1px solid ${navPalette.border}`,
                      backgroundColor: 'rgba(15, 23, 42, 0.97)',
                      color: navPalette.text,
                      boxShadow: '0 12px 24px rgba(15, 23, 42, 0.28)',
                      '& .MuiMenuItem-root': {
                        fontSize: '0.75rem',
                        gap: 0.75,
                        borderRadius: 1.5,
                        '&:hover': {
                          backgroundColor: 'rgba(96, 165, 250, 0.18)',
                        },
                      },
                      '& .MuiListItemIcon-root': {
                        color: navPalette.accent,
                        minWidth: 28,
                      },
                    },
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      navigate('/profile')
                      handleProfileMenuClose()
                    }}
                  >
                    <ListItemIcon>
                      <ProfileIcon fontSize="small" />
                    </ListItemIcon>
                    {t('navbar.profile')}
                  </MenuItem>
                  <Divider sx={{ borderColor: navPalette.border }} />
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    {t('navbar.logout')}
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                color="inherit"
                startIcon={<LoginIcon fontSize="small" />}
                onClick={() => navigate('/login')}
                sx={{
                  ...desktopButtonSx,
                  border: `1px solid ${navPalette.border}`,
                  backgroundColor: 'rgba(15, 23, 42, 0.28)',
                }}
              >
                {t('navbar.login')}
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobil Drawer (Hamburger Menü) */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 230,
            backgroundColor: '#0f172a',
            color: navPalette.text,
            borderRight: `1px solid ${navPalette.border}`,
            backgroundImage: 'linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(30,58,138,0.92) 100%)',
          },
        }}
      >
        <Box sx={{ width: 230, py: 1 }} role="presentation">
          <List>
            {menuItems.map((item, index) =>
              item.visible ? (
                item.subMenu ? (
                  // Raporlar veya Ayarlar gibi alt menü varsa, burada alt öğeleri liste halinde gösteriyoruz
                  <React.Fragment key={index}>
                    <ListItem
                      sx={{
                        py: 0.6,
                        px: 1.5,
                        borderRadius: 2,
                        mb: 0.25,
                        backgroundColor: 'rgba(15, 23, 42, 0.45)',
                      }}
                    >
                      <ListItemIcon sx={{ color: navPalette.accent }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: '0.74rem',
                          fontWeight: 600,
                          color: navPalette.text,
                        }}
                      />
                    </ListItem>
                    {/* Alt öğeleri sıralıyoruz */}
                    {item.subMenu.map(
                      (sub, subIndex) =>
                        sub.visible && (
                          <ListItem
                            button
                            key={subIndex}
                            sx={{
                              pl: 3.5,
                              pr: 1.5,
                              borderRadius: 2,
                              mx: 1,
                              mb: 0.25,
                              '&:hover': {
                                backgroundColor: navPalette.hover,
                              },
                            }} // Biraz iç boşluk
                            onClick={() => {
                              sub.onClick()
                              handleDrawerToggle()
                            }}
                          >
                            <ListItemIcon sx={{ color: navPalette.accent }}>
                              {sub.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={sub.text}
                              primaryTypographyProps={{
                                fontSize: '0.72rem',
                                color: navPalette.text,
                              }}
                            />
                          </ListItem>
                        )
                    )}
                  </React.Fragment>
                ) : (
                  <ListItem
                    button
                    key={index}
                    sx={{
                      mx: 1,
                      mb: 0.25,
                      px: 1.5,
                      borderRadius: 2,
                      '&:hover': {
                        backgroundColor: navPalette.hover,
                      },
                    }}
                    onClick={() => {
                      item.onClick()
                      handleDrawerToggle()
                    }}
                  >
                    <ListItemIcon sx={{ color: navPalette.accent }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.74rem',
                        color: navPalette.text,
                      }}
                    />
                  </ListItem>
                )
              ) : null
            )}
          </List>

          <Divider sx={{ borderColor: navPalette.border, my: 1 }} />

          {/* Dil Değiştirme Butonları (Mobil) */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => changeLanguage('en')}
              sx={{ ...languageButtonSx, mr: 1 }}
            >
              EN
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => changeLanguage('tr')}
              sx={{ ...languageButtonSx, mr: 1 }}
            >
              TR
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => changeLanguage('de')}
              sx={{ ...languageButtonSx, mr: 1 }}
            >
              DE
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => changeLanguage('fr')}
              sx={{ ...languageButtonSx, mr: 1 }}
            >
              FR
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => changeLanguage('es')}
              sx={{ ...languageButtonSx, mr: 1 }}
            >
              ES
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => changeLanguage('it')}
              sx={{ ...languageButtonSx, mr: 1 }}
            >
              IT
            </Button>
          </Box>
          <Divider sx={{ mt: 1, borderColor: navPalette.border }} />

          {currentUser ? (
            <List>
              <ListItem
                button
                onClick={() => {
                  navigate('/profile')
                  handleDrawerToggle()
                }}
                sx={{
                  mx: 1,
                  mb: 0.25,
                  px: 1.5,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: navPalette.hover,
                  },
                }}
              >
                <ListItemIcon sx={{ color: navPalette.accent }}>
                  <ProfileIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t('navbar.profile')}
                  primaryTypographyProps={{
                    fontSize: '0.74rem',
                    color: navPalette.text,
                  }}
                />
              </ListItem>
              <ListItem
                button
                onClick={() => {
                  handleLogout()
                  handleDrawerToggle()
                }}
                sx={{
                  mx: 1,
                  mb: 0.25,
                  px: 1.5,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: navPalette.hover,
                  },
                }}
              >
                <ListItemIcon sx={{ color: navPalette.accent }}>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t('navbar.logout')}
                  primaryTypographyProps={{
                    fontSize: '0.74rem',
                    color: navPalette.text,
                  }}
                />
              </ListItem>
            </List>
          ) : (
            <List>
              <ListItem
                button
                onClick={() => {
                  navigate('/login')
                  handleDrawerToggle()
                }}
                sx={{
                  mx: 1,
                  mb: 0.25,
                  px: 1.5,
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: navPalette.hover,
                  },
                }}
              >
                <ListItemIcon sx={{ color: navPalette.accent }}>
                  <LoginIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={t('navbar.login')}
                  primaryTypographyProps={{
                    fontSize: '0.74rem',
                    color: navPalette.text,
                  }}
                />
              </ListItem>
            </List>
          )}
        </Box>
      </Drawer>

       {/* Help Modal */}
        <Dialog
          open={helpModalOpen}
          onClose={() => {
            setHelpModalOpen(false);
            setSelectedVideo(null);
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            },
          }}
        >
          <DialogTitle>
            <Box display="flex" alignItems="center">
              {selectedVideo ? (
                <>
                  <PlayArrow sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">{t('help.watchingVideo')}</Typography>
                </>
              ) : (
                <Typography variant="h6">{t('help.title')}</Typography>
              )}
            </Box>
            <IconButton
              aria-label="kapat"
              onClick={() => setHelpModalOpen(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {selectedVideo ? (
              <Box sx={{ position: 'relative', paddingTop: '56.25%' /* 16:9 oran */ }}>
                <video
                  controls
                  autoPlay
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: '8px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                  }}
                >
                  <source src={videoUrls[selectedVideo - 1]} type="video/mp4" />
                </video>
              </Box>
            ) : (
              <Grid container spacing={2} sx={{ py: 2 }}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((videoNum) => (
                  <Grid item xs={12} sm={6} md={4} key={videoNum}>
                    <MuiButton
                      fullWidth
                      variant="outlined"
                      onClick={() => setSelectedVideo(videoNum)}
                      startIcon={<PlayArrow />}
                      sx={{
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        borderRadius: '8px',
                        '&:hover': {
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        },
                      }}
                    >
                      {t(`help.video${videoNum}`)}
                    </MuiButton>
                  </Grid>
                ))}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            {selectedVideo && (
              <MuiButton
                onClick={() => setSelectedVideo(null)}
                startIcon={<ArrowBack />}
                sx={{ textTransform: 'none' }}
              >
                {t('help.back')}
              </MuiButton>
            )}
            <MuiButton
              onClick={() => setHelpModalOpen(false)}
              color="primary"
              variant="contained"
              sx={{ borderRadius: '8px' }}
            >
              {t('help.close')}
            </MuiButton>
          </DialogActions>
        </Dialog>
    </>
  )
}
